import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { showBanner } from 'workflow/common/common.actions'

import { sdkTagsKeys } from 'common/api/queryKeys/sdkTagsKeys'
import Button from 'common/components/button'
import Modal from 'common/components/Modal'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import { SdkTag, updateSdkTag } from './api'

type Props = {
  tag: SdkTag
  open: boolean
  onClose: () => void
}

const normalizeDomain = (input: string): string =>
  input
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase()

// A leading "*." wildcard allows the domain and any of its subdomains
const isValidDomain = (domain: string): boolean =>
  /^(\*\.)?[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/.test(
    domain
  )

const EditTagModal = ({ tag, open, onClose }: Props) => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const [name, setName] = useState(tag.name)
  const [nameError, setNameError] = useState('')
  const [domains, setDomains] = useState<string[]>(tag.domains ?? [])
  const [domainInput, setDomainInput] = useState('')
  const [domainError, setDomainError] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const handleAddDomain = () => {
    const normalized = normalizeDomain(domainInput)
    if (!normalized) {
      return
    }
    if (!isValidDomain(normalized)) {
      setDomainError(
        'Enter a valid domain, e.g. shop.example.com or *.example.com'
      )
      return
    }
    if (domains.includes(normalized)) {
      setDomainError('This domain is already in the list.')
      return
    }
    setDomains((prev) => [...prev, normalized])
    setDomainInput('')
    setDomainError('')
  }

  const handleRemoveDomain = (domain: string) => {
    setDomains((prev) => prev.filter((d) => d !== domain))
  }

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setNameError('Name is required.')
      return
    }
    if (trimmed.length > 64) {
      setNameError('Name must be 64 characters or fewer.')
      return
    }
    setIsSaving(true)
    try {
      const updated = await updateSdkTag({
        accountId,
        id: tag._id,
        name: trimmed,
        domains,
      })
      queryClient.setQueryData<SdkTag[]>(sdkTagsKeys.list(accountId), updated)
      dispatch(showBanner({ type: 'success', content: 'Tag updated' }))
      onClose()
    } catch {
      dispatch(
        showBanner({
          type: 'error',
          content: 'Failed to update tag. Please try again.',
        })
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    setName(tag.name)
    setNameError('')
    setDomains(tag.domains ?? [])
    setDomainInput('')
    setDomainError('')
    onClose()
  }

  return (
    <Modal
      visible={open}
      onClose={handleClose}
      title="Edit tag"
      footer={
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="mt-2 space-y-6">
        {/* Name */}
        <div>
          <label
            htmlFor="sdk-edit-tag-name"
            className="mb-1 block text-sm font-medium text-coolGray-800"
          >
            Name
          </label>
          <input
            id="sdk-edit-tag-name"
            data-testid="sdk-edit-tag-name-input"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (nameError) {
                setNameError('')
              }
            }}
            maxLength={64}
            className="w-full rounded border border-coolGray-300 px-3 py-2 text-sm focus:border-maroon-400 focus:outline-none"
          />
          {nameError && (
            <p className="mt-1 text-sm text-red-500">{nameError}</p>
          )}
        </div>

        {/* Domains */}
        <div>
          <p className="mb-1 text-sm font-medium text-coolGray-800">
            Allowed domains
          </p>
          <p className="mb-3 text-sm text-coolGray-500">
            Jacquard will only accept conversions from listed domains. Domains
            match exactly — example.com will not match shop.example.com. To also
            include all subdomains, use a wildcard: *.example.com. Leave empty
            to allow all domains.
          </p>

          <div className="flex gap-2">
            <input
              data-testid="sdk-domain-input"
              type="text"
              value={domainInput}
              onChange={(e) => {
                setDomainInput(e.target.value)
                if (domainError) {
                  setDomainError('')
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddDomain()
                }
              }}
              placeholder="shop.example.com or *.example.com"
              className="flex-1 rounded border border-coolGray-300 px-3 py-2 text-sm focus:border-maroon-400 focus:outline-none"
            />
            <Button
              data-testid="sdk-domain-add-button"
              variant="secondary"
              onClick={handleAddDomain}
            >
              Add
            </Button>
          </div>
          {domainError && (
            <p className="mt-1 text-sm text-red-500">{domainError}</p>
          )}

          {domains.length > 0 && (
            <ul
              data-testid="sdk-domain-list"
              className="mt-3 divide-y divide-coolGray-100 rounded border border-coolGray-200"
            >
              {domains.map((domain) => (
                <li
                  key={domain}
                  data-testid="sdk-domain-item"
                  className="flex items-center justify-between px-3 py-2"
                >
                  <span className="font-sourceCodePro text-sm text-coolGray-800">
                    {domain}
                  </span>
                  <button
                    type="button"
                    data-testid="sdk-domain-remove"
                    aria-label={`Remove ${domain}`}
                    onClick={() => handleRemoveDomain(domain)}
                    className="ml-4 text-coolGray-400 hover:text-red-500 text-lg leading-none"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {domains.length === 0 && (
            <p className="mt-3 text-sm text-coolGray-400 italic">
              No domains added — all domains are currently allowed.
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default EditTagModal
